import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playScratchSound, playWinFanfare } from '../utils/soundEffects';
import { getSettings } from '../utils/db';

const { FiGift, FiAward } = FiIcons;

const ScratchToRevealCard = ({ reward, onRevealed, compact = false }) => {
  const canvasRef = useRef(null);
  const lastPosRef = useRef(null);
  const lastScratchAudioRef = useRef(0);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isScratching, setIsScratching] = useState(false);
  const brushRadius = compact ? 28 : 40;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Set canvas resolution to match display size for sharpness
    const rect = canvas.parentElement.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Draw the scratch-off layer (Silver/Gold gradient)
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#94a3b8'); // slate-400
    gradient.addColorStop(0.5, '#cbd5e1'); // slate-300
    gradient.addColorStop(1, '#64748b'); // slate-500
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add overlay pattern/text
    ctx.font = `bold ${compact ? '18px' : '24px'} Inter, sans-serif`;
    ctx.fillStyle = '#1e293b'; // slate-800
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('SCRATCH HERE', canvas.width / 2, canvas.height / 2);
    
    // Add some noise/texture to look like a scratch card
    for(let i = 0; i < 2000; i++) {
      ctx.fillStyle = Math.random() > 0.5 ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
      ctx.fillRect(Math.random() * canvas.width, Math.random() * canvas.height, 2, 2);
    }
  }, [compact]);

  const getPointerPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const handleStart = (e) => {
    setIsScratching(true);
    const pos = getPointerPos(e);
    lastPosRef.current = pos;
    scratch(e);
  };

  const scratch = (e) => {
    if (isRevealed) return;
    
    // Touch movements should trigger scratching only if we started scratching
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPointerPos(e);

    if (e.type === 'touchmove' || e.type === 'mousemove') {
      if (!isScratching) return;
    }
    
    e.preventDefault(); // Prevent scrolling while scratching

    ctx.globalCompositeOperation = 'destination-out';
    
    if (lastPosRef.current) {
      ctx.beginPath();
      ctx.lineWidth = brushRadius * 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, brushRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    lastPosRef.current = pos;

    // Throttle scratch sound every 90ms
    const now = Date.now();
    if (now - lastScratchAudioRef.current > 90) {
      lastScratchAudioRef.current = now;
      const settings = getSettings();
      if (settings.soundEnabled !== false) {
        playScratchSound();
      }
    }

    checkReveal();
  };

  const handleEnd = () => {
    setIsScratching(false);
    lastPosRef.current = null;
  };

  const checkReveal = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    
    let transparentPixels = 0;
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] === 0) {
        transparentPixels++;
      }
    }

    const totalPixels = pixels.length / 4;
    const percentage = (transparentPixels / totalPixels) * 100;

    // Lowered threshold from 40% to 25% for much faster auto-reveal
    if (percentage > 25 && !isRevealed) {
      setIsRevealed(true);
      const settings = getSettings();
      if (settings.soundEnabled !== false) {
        playWinFanfare();
      }
      setTimeout(() => {
        if (onRevealed) onRevealed();
      }, 1500); // Wait 1.5s before moving to next screen
    }
  };

  return (
    <div className={`w-full ${compact ? 'max-w-[260px]' : 'max-w-[320px] sm:max-w-sm'} mx-auto aspect-[3/4] relative rounded-2xl overflow-hidden shadow-2xl border-4 border-slate-700 bg-gradient-to-br from-amber-100 to-orange-200 flex flex-col items-center justify-center p-4 sm:p-6 text-center select-none`}>
      
      {/* Background (The Reward) */}
      <div className="relative z-10 flex flex-col items-center">
        <div className={`${compact ? 'w-16 h-16 mb-3' : 'w-22 h-22 mb-5'} rounded-full bg-white shadow-xl flex items-center justify-center`}>
          {reward.includes('Luck') ? (
            <SafeIcon icon={FiAward} className={`${compact ? 'text-3xl' : 'text-5xl'} text-slate-400`} />
          ) : (
            <SafeIcon icon={FiGift} className={`${compact ? 'text-3xl' : 'text-5xl'} text-orange-500`} />
          )}
        </div>
        <h4 className="text-xs font-bold text-orange-600 uppercase tracking-widest mb-1">You Won</h4>
        <h2 className={`${compact ? 'text-xl' : 'text-2xl sm:text-3xl'} font-extrabold text-slate-800 leading-tight px-2`}>
          {reward}
        </h2>
      </div>

      {/* Foreground (The Scratch Layer) */}
      <motion.canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full z-20 cursor-crosshair touch-none"
        animate={{ opacity: isRevealed ? 0 : 1 }}
        transition={{ duration: 0.5 }}
        onMouseDown={handleStart}
        onMouseMove={scratch}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={scratch}
        onTouchEnd={handleEnd}
        style={{ pointerEvents: isRevealed ? 'none' : 'auto' }}
      />
    </div>
  );
};

export default ScratchToRevealCard;